import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DragData, useElementDrag } from 'builder/hooks/useElementDrag'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import {
  Drawer,
  Header,
  Pull,
  PullContainer,
  ContentContainer,
  BackDrop,
  openedHeight,
} from './styles'
import { EditDrawerProps } from './types'

const animationDuration = 500
const swipeDistanceThresholdPx = 60

export const EditDrawer: React.FC<EditDrawerProps> = props => {
  const { isOpen, onToggle, children, onInitialHeightChange } = props
  const [isLocalOpen, setIsLocalOpen] = useState(false)
  const [initialHeight, setInitialHeight] = useState('auto')
  const timeout = useRef<NodeJS.Timeout>()
  const pullRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { isTablet } = useMediaQueries()

  useEffect(() => {
    if (isOpen !== undefined) {
      setIsLocalOpen(isOpen)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        if (containerRef.current) {
          setInitialHeight(`${containerRef.current.clientHeight}px`)
        }
      }, animationDuration)

      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  useEffect(() => () => clearTimeout(timeout.current), [])

  const onLocalToggle = useCallback(() => {
    const newValue = !isLocalOpen
    setIsLocalOpen(!isLocalOpen)

    if (newValue) {
      onToggle?.()
    } else {
      clearTimeout(timeout.current)
      timeout.current = setTimeout(() => onToggle?.(), animationDuration)
    }
  }, [isLocalOpen, onToggle])

  const onDragEnd = useCallback(
    (event: DragData) => {
      if (
        !!event.deltaY &&
        ((!isLocalOpen && event.deltaY < swipeDistanceThresholdPx) ||
          (isLocalOpen && event.deltaY > swipeDistanceThresholdPx))
      ) {
        onLocalToggle()
      }
    },
    [onLocalToggle, isLocalOpen],
  )

  const { isDragging, draggingPosition } = useElementDrag({ elementRef: pullRef, onDragEnd })

  const deltaY = isDragging ? -draggingPosition.deltaY : 0
  const height = isLocalOpen ? openedHeight : initialHeight
  const calcHeight = height === initialHeight ? initialHeight : `calc(${height} + ${deltaY}px)`

  // this prevents bug when we change orientation of tablet devices and
  // drawer can't calculate initial height correctly
  useEffect(() => {
    if (isTablet && isOpen) {
      setInitialHeight('auto')
      onLocalToggle()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTablet])

  useEffect(() => {
    onInitialHeightChange?.(initialHeight)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHeight])

  return (
    <>
      {isOpen && <BackDrop onClick={onLocalToggle} />}
      <Drawer
        ref={containerRef}
        $animationDurationMs={animationDuration}
        $isDragging={isDragging}
        style={{
          height: calcHeight,
          minHeight: initialHeight,
        }}
      >
        <Header ref={pullRef}>
          <PullContainer onClick={onLocalToggle}>
            <Pull />
          </PullContainer>
        </Header>
        <ContentContainer>{children}</ContentContainer>
      </Drawer>
    </>
  )
}
